import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Mail, Phone, Send, X } from "lucide-react";
import { api } from "@/lib/api";

export default function ContactFab() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const ref = useRef(null);
  const location = useLocation();
  // Product pages have their own fixed bottom bar (price + "В корзину") on
  // mobile - sit above it there. Everywhere else (e.g. the catalog's
  // floating "Фильтры" button) this can sit at the same low height.
  const isProductPage = location.pathname.startsWith("/product/");

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!settings) return null;

  const options = [
    settings.contact_email && {
      key: "email",
      label: "Email",
      href: `mailto:${settings.contact_email}`,
      icon: Mail,
    },
    settings.contact_phone && {
      key: "phone",
      label: "Телефон",
      href: `tel:${settings.contact_phone.replace(/[^\d+]/g, "")}`,
      icon: Phone,
    },
    settings.telegram_url && {
      key: "telegram",
      label: "Telegram",
      href: settings.telegram_url,
      icon: Send,
      external: true,
    },
    settings.whatsapp_url && {
      key: "whatsapp",
      label: "WhatsApp",
      href: settings.whatsapp_url,
      icon: MessageCircle,
      external: true,
    },
  ].filter(Boolean);

  if (options.length === 0) return null;

  return (
    <div
      ref={ref}
      data-testid="contact-fab"
      className={`fixed right-4 z-[80] flex flex-col items-end gap-3 md:bottom-6 md:right-6 ${isProductPage ? "bottom-24" : "bottom-5"}`}
    >
      {open && (
        <div className="flex flex-col gap-2 rounded-2xl border border-line bg-white p-2 shadow-2xl">
          {options.map(({ key, label, href, icon: Icon, external }) => (
            <a
              key={key}
              href={href}
              target={external ? "_blank" : undefined}
              rel={external ? "noreferrer" : undefined}
              data-testid={`contact-fab-${key}`}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-black/5"
            >
              <Icon size={17} />
              {label}
            </a>
          ))}
        </div>
      )}
      <button
        type="button"
        data-testid="contact-fab-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Закрыть" : "Написать"}
        className="flex h-12 w-12 items-center justify-center gap-2 rounded-full bg-ink text-white shadow-2xl transition-transform hover:-translate-y-0.5 md:h-14 md:w-auto md:px-5 md:text-sm md:font-semibold"
      >
        {open ? <X size={18} /> : <MessageCircle size={18} />}
        <span className="hidden md:inline">{!open && "Написать"}</span>
      </button>
    </div>
  );
}
